import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../axiosConfig';
import Header from '../Components/Header';
import Sidebar from '../Components/Sidebar';
import moment from 'moment';
import { Link } from 'react-router-dom';
import '../styles.css';

const STATUS_LABELS = {
  present: 'P',
  absent: 'A',
  half_day: 'HD',
  'half-day': 'HD',
  weekly_off: 'WO',
  week_off: 'WO',
};


const getStatusKey = (rawStatus) => {
  if (!rawStatus) return '';
  const s = String(rawStatus).toLowerCase();
  if (s === 'present' || s === 'p') return 'present';
  if (s === 'absent' || s === 'a') return 'absent';
  if (s === 'half_day' || s === 'half-day' || s === 'hd' || s === 'halfday' || s === 'half day') return 'half_day';
  return s;
};

const AttendanceAdmin = () => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const [currentUser, setCurrentUser] = useState({});
  const [currentUserId] = useState(localStorage.getItem('id'));

  const [users, setUsers] = useState([]);
  const [attendance, setAttendance] = useState([]);

  // Approved leave mapping for the selected month
  // Key: `${userId}|${YYYY-MM-DD}` -> { leaveTypeName: string }
  const [approvedLeavesByUserByDate, setApprovedLeavesByUserByDate] = useState(new Map());

  const [leavesLoading, setLeavesLoading] = useState(false);



  const now = new Date();
  const defaultYear = now.getFullYear();
  const defaultMonth = String(now.getMonth() + 1).padStart(2, '0');

  const [selectedYear, setSelectedYear] = useState(String(defaultYear));
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [statusFilter, setStatusFilter] = useState('all'); // all | present | absent | half_day

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible((v) => !v);

  useEffect(() => {
    axiosInstance
      .get('/user/')
      .then((response) => {
        setUsers(response.data || []);
        return axiosInstance.get(`/user/${currentUserId}/`);
      })
      .then((res) => {
        const userData = res.data;
        setCurrentUser({
          ...userData,
          role: userData.role?.name || userData.role?.[0]?.name || 'guest',
        });
      })
      .catch(console.error);
  }, [currentUserId]);

  // Reuse same attendance API used in dashboard
  useEffect(() => {
    // dashboard uses: /get-attendance/
    // we fetch all and filter by month client-side to keep API reliance minimal.
    axiosInstance
      .get('/get-attendance/')
      .then((res) => setAttendance(res.data || []))
      .catch(console.error);
  }, []);

  const activeUsers = useMemo(() => {
    return (users || []).filter((u) => u.is_active).sort((a, b) => (a.employee_code || '').localeCompare(b.employee_code || ''));
  }, [users]);

  const daysInMonth = useMemo(() => {
    const start = moment(`${selectedYear}-${selectedMonth}-01`, 'YYYY-MM-DD');
    const days = start.daysInMonth();
    return Array.from({ length: days }, (_, i) => {
      const d = moment(start).add(i, 'day');
      return d;
    });
  }, [selectedYear, selectedMonth]);

  // Generate WO (week off) for every Sunday in the selected month/year
  const woByUserByDate = useMemo(() => {
    const map = new Map();
    const userIds = (users || []).map((u) => u.id);

    for (const uId of userIds) {
      for (const d of daysInMonth) {
        // moment().day(): Sunday => 0
        if (d.day() === 0) {
          const dateKey = d.format('YYYY-MM-DD');
          map.set(`${uId}|${dateKey}`, 'weekly_off');
        }
      }
    }

    return map;
  }, [users, daysInMonth]);


  const attendanceByUserByDate = useMemo(() => {
    // Key: `${userId}|${YYYY-MM-DD}` -> { status, punch_in_time, punch_out_time }
    const map = new Map();
    for (const a of attendance || []) {
      const dateKey = a.date ? moment(a.date).format('YYYY-MM-DD') : '';
      const userId = a.user;
      if (!userId || !dateKey) continue;
      map.set(`${userId}|${dateKey}`, a);
    }
    return map;
  }, [attendance]);

  // Fetch approved leave requests for selected month/year and expand into per-day map
  useEffect(() => {
    let isCancelled = false;

    const fetchApprovedLeaves = async () => {
      setLeavesLoading(true);
      try {
        // Expected shape based on LeaveDecision/LeaveTransactions:
        // { user, user_id, user_name, leave_type, leave_type_name, leave_type_code,
        //   start_date, end_date, status }
        // Backend endpoint returns:
        // { users: [...], approved_leaves: [{ user_id, leave_type_code, leave_type_name, start_date, end_date, status, ...}] }
        const res = await axiosInstance.get('/all-users-leave-dashboard/');
        const payload = res?.data || {};
        const approvedLeaves = payload.approved_leaves || [];

        const map = new Map();

        // Build per-day entries for the server-provided month boundaries.
        // If API already clamps, this is still safe.
        const monthStart = payload.month_start ? moment(payload.month_start, 'YYYY-MM-DD').startOf('day') : moment(`${selectedYear}-${selectedMonth}-01`, 'YYYY-MM-DD').startOf('day');
        const monthEnd = payload.month_end ? moment(payload.month_end, 'YYYY-MM-DD').endOf('day') : moment(monthStart).endOf('month');

        for (const lr of approvedLeaves || []) {
          const userId = lr.user_id;
          const start = lr.start_date ? moment(lr.start_date, 'YYYY-MM-DD') : null;
          const end = lr.end_date ? moment(lr.end_date, 'YYYY-MM-DD') : null;
          if (!userId || !start || !end) continue;

          const leaveTypeName = lr.leave_type_name || lr.leave_type_code || 'Leave';

          const effectiveStart = start.isBefore(monthStart) ? monthStart : start;
          const effectiveEnd = end.isAfter(monthEnd) ? monthEnd : end;
          if (effectiveEnd.isBefore(effectiveStart)) continue;

          const daysCount = effectiveEnd.diff(effectiveStart, 'days');
          for (let i = 0; i <= daysCount; i++) {
            const d = moment(effectiveStart).add(i, 'day');
            const dateKey = d.format('YYYY-MM-DD');
            map.set(`${userId}|${dateKey}`, { leaveTypeName });
          }
        }

        if (!isCancelled) setApprovedLeavesByUserByDate(map);

      } catch (e) {
        // If API params aren't supported yet, fail gracefully.
        console.error('Failed to fetch approved leave for attendance admin:', e);
        if (!isCancelled) setApprovedLeavesByUserByDate(new Map());
      } finally {
        if (!isCancelled) setLeavesLoading(false);
      }
    };

    fetchApprovedLeaves();

    return () => {
      isCancelled = true;
    };
  }, [selectedYear, selectedMonth]);




  const statusCellClass = (status) => {
    const key = getStatusKey(status);
    if (key === 'present') return 'attendance-status attendance-present';
    if (key === 'half_day') return 'attendance-status attendance-halfday';
    if (key === 'absent') return 'attendance-status attendance-absent';
    return 'attendance-status attendance-empty';
  };

  const statusCellText = (status) => {
    const key = getStatusKey(status);
    if (!key) return '—';

    if (STATUS_LABELS[key]) return STATUS_LABELS[key];

    return String(status || '—');
  };


  const matchesFilter = (status, leaveTypeNameForDate) => {
    // WO should not be affected by filter; it is always shown on Sundays.
    if (status === 'weekly_off' || status === 'week_off') return true;

    // Leave overrides attendance (status cell shows L). Filter applies to attendance only,
    // but if you want filtering by leave in the future, we can extend this.
    if (leaveTypeNameForDate) {
      // Only show leaves if filter is all; otherwise keep original attendance filtering.
      return statusFilter === 'all';
    }

    const key = getStatusKey(status);
    if (statusFilter === 'all') return true;
    if (statusFilter === 'present') return key === 'present';
    if (statusFilter === 'absent') return key === 'absent';
    if (statusFilter === 'half_day') return key === 'half_day';
    return true;
  };


  const downloadCSV = async () => {
    if (!selectedYear || !selectedMonth) return;
    try {
      const res = await axiosInstance.get(`/attendance/csv/${selectedYear}-${selectedMonth}/`, { responseType: 'blob' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(new Blob([res.data]));
      link.download = `attendance_${selectedYear}-${selectedMonth}.csv`;
      link.click();
    } catch (e) {
      alert('No attendance records found for selected month.');
   }
  };

  const years = Array.from({ length: 8 }, (_, i) => String(new Date().getFullYear() - i));
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        currentUser={currentUser}
        activeRole={currentUser.role}
        error={''}
        handleSwitchRole={null}
        handleLogout={() => {
          localStorage.clear();
          window.location.href = '/';
        }}
      />

      <main className="dashboard-main-content">
        <div className="dashboard-header-section">
          <h1>Attendance Admin</h1>
          <p>Day-wise attendance grid for active users</p>
        </div>

        <div className="attendance-admin-controls">

          <div className="filter-row" style={{ marginBottom: 0 }}>
            <div className="download-group">
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {months.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <button className="action-btn" onClick={downloadCSV}>Export</button>
            </div>

            <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All statuses</option>
              <option value="present">Present (P)</option>
              <option value="half_day">Half Day (HD)</option>
              <option value="absent">Absent (A)</option>
            </select>
          </div>
        </div>

        <div className="attendance-admin-legend">
          <div className="legend-item"><span className="legend-dot present"></span> P (Present)</div>
          <div className="legend-item"><span className="legend-dot half-day"></span> HD (Half Day)</div>
          <div className="legend-item"><span className="legend-dot absent"></span> A (Absent)</div>
          <div className="legend-item"><span className="legend-dot leave"></span> L (Approved Leave)</div>
        </div>


        <div className="attendance-admin-table-wrap">
          <table className="attendance-admin-table">
            <thead>
              <tr>
                <th className="sticky-left">Name</th>

                {daysInMonth.map((d) => (
                  <th key={d.format('YYYY-MM-DD')}>
                    {d.format('DD')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activeUsers.map((u) => (
                <tr key={u.id}>
                  <td className="sticky-left">

                    <Link to={`/employee-calendar/${u.id}`}>{u.first_name} {u.last_name}</Link>
                  </td>
                  {daysInMonth.map((d) => {
                    const dateKey = d.format('YYYY-MM-DD');
                    const entry = attendanceByUserByDate.get(`${u.id}|${dateKey}`);
                    const rawStatus = entry?.status;

                    // WO for Sundays (week off)
                    const woStatus = woByUserByDate.get(`${u.id}|${dateKey}`);
                    const finalStatus = woStatus ? woStatus : rawStatus;


                    // Approved leave overrides attendance
                    const leaveForDay = approvedLeavesByUserByDate.get(`${u.id}|${dateKey}`);
                    const isOnLeave = Boolean(leaveForDay?.leaveTypeName);
                    const leaveTypeName = leaveForDay?.leaveTypeName;

                    if (isOnLeave) {
                      // If on approved leave, show leave (not WO)

                      return (
                        <td key={dateKey}>
                          <span
                            className="attendance-status attendance-leave"
                            title={`Approved Leave: ${leaveTypeName}`}
                          >
                            L
                          </span>
                        </td>
                      );
                    }

                    // If filter doesn't match, show empty
                    const showStatus = matchesFilter(finalStatus, null) ? finalStatus : '';


                    return (
                      <td key={dateKey}>
                        <span className={statusCellClass(showStatus)}>
                          {statusCellText(showStatus)}
                        </span>
                      </td>
                    );
                  })}

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AttendanceAdmin;

